/* eslint-disable react/no-unescaped-entities */
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Header, Footer } from "../components/Layout";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import {
  ArrowLeft, Star, MagicWand, Trash, CheckCircle, MagnifyingGlass,
} from "@phosphor-icons/react";
import { toast } from "sonner";

// SUNFLOWER-SEED theme
const TH = {
  bg:        "#11120A",
  surface:   "#1A1B12",
  surfaceHi: "#26271B",
  border:    "#32341F",
  borderHi:  "#4E5230",
  accent:    "#FFE177",
  accentDim: "#B69330",
  warm:      "#FFB36B",
  green:     "#9FE39B",
  pop:       "#FFD17B",
  text:      "#FFF7DD",
  textMute:  "#A89E7B",
  textDim:   "#6E6645",
};

export default function AdminSeedReviewsPage() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [astros, setAstros] = useState([]);
  const [poolInfo, setPoolInfo] = useState({ name_pool_size: 0, comment_pool_size: 0 });
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [filterText, setFilterText] = useState("");
  const [count, setCount] = useState(5);
  const [spanDays, setSpanDays] = useState(28);
  const [submitting, setSubmitting] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  useEffect(() => {
    if (authLoading) return;
    if (!user || !user.is_super_admin) {
      toast.error("Seed Reviews is a Super-Admin tool.");
      navigate("/", { replace: true });
    }
  }, [user, authLoading, navigate]);

  const fetchAstros = () => {
    api.get("/super-admin/seed-reviews/astrologers")
      .then(({ data }) => {
        setAstros(data.astrologers || []);
        setPoolInfo({
          name_pool_size:    data.name_pool_size    || 0,
          comment_pool_size: data.comment_pool_size || 0,
        });
      })
      .catch((err) => toast.error(err?.response?.data?.detail || "Failed to load astrologers."));
  };

  useEffect(() => {
    if (!user?.is_super_admin) return;
    fetchAstros();
    // eslint-disable-next-line
  }, [user]);

  const filtered = useMemo(() => {
    const q = filterText.trim().toLowerCase();
    if (!q) return astros;
    return astros.filter((a) =>
      (a.name || "").toLowerCase().includes(q)
      || (a.specialization || "").toLowerCase().includes(q)
      || (a.technique || "").toLowerCase().includes(q),
    );
  }, [astros, filterText]);

  const toggle = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const selectAllVisible = () => setSelectedIds(new Set(filtered.map((a) => a.astrologer_id)));
  const clearSelection = () => setSelectedIds(new Set());

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (selectedIds.size === 0) {
      toast.error("Pick at least one astrologer.");
      return;
    }
    if (count < 1 || count > 100) {
      toast.error("Count must be between 1 and 100.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/super-admin/seed-reviews", {
        astrologer_ids: [...selectedIds],
        count: parseInt(count, 10),
        span_days: parseInt(spanDays, 10),
      });
      setLastResult(data);
      toast.success(
        `Added ${data.total_inserted} reviews across ${data.astrologers_seeded} astrologers`,
      );
      fetchAstros();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to seed reviews.");
    } finally {
      setSubmitting(false);
    }
  };

  const undoAll = async () => {
    if (selectedIds.size !== 1) {
      toast.info("Pick exactly ONE astrologer to undo their seeded reviews.");
      return;
    }
    const id = [...selectedIds][0];
    const a = astros.find((x) => x.astrologer_id === id);
    if (!window.confirm(`Remove ALL admin-seeded reviews from ${a?.name || "this astrologer"}? Real (chat-based) reviews will stay.`)) return;
    try {
      const { data } = await api.delete(`/super-admin/seed-reviews/${id}/all`);
      toast.success(`Removed ${data.removed} seeded reviews.`);
      setLastResult(null);
      fetchAstros();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to remove seeded reviews.");
    }
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: TH.bg, color: TH.text }}>
      <Header />
      <main className="mx-auto max-w-3xl px-4 sm:px-6 py-10 sm:py-14">
        <button type="button" onClick={() => navigate(-1)}
          className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.18em] mb-7 hover:opacity-100"
          style={{ color: TH.textMute, opacity: 0.7 }}
          data-testid="seed-back">
          <ArrowLeft size={12} weight="bold" /> Back
        </button>

        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full font-mono text-[10px] uppercase tracking-[0.25em] mb-5"
          style={{ backgroundColor: `${TH.accent}1A`, color: TH.accent, border: `1px solid ${TH.accentDim}` }}>
          <MagicWand size={11} weight="bold" /> Super-Admin · Seed Reviews
        </div>

        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-light leading-tight">
          Seed <span className="italic" style={{ color: TH.accent }}>5-star reviews</span>
        </h1>
        <p className="mt-4 max-w-2xl text-base leading-relaxed font-light" style={{ color: TH.textMute }}>
          Pick one or many astrologers; each one gets <b style={{ color: TH.text }}>N</b> fresh
          5-star reviews. Names drawn from a pool of <b style={{ color: TH.text }}>{poolInfo.name_pool_size}</b>,
          blurbs from <b style={{ color: TH.text }}>{poolInfo.comment_pool_size}</b>, dates randomly
          staggered (newest at top) over your chosen window so the listing looks organic.
        </p>

        <form onSubmit={submit} className="mt-8 rounded-2xl p-6 space-y-6"
          style={{ background: TH.surface, border: `1px solid ${TH.border}` }}
          data-testid="seed-form">

          {/* Astrologer multi-select */}
          <div>
            <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
              <label className="text-xs uppercase tracking-wider font-mono" style={{ color: TH.textMute }}>
                Astrologers · <span style={{ color: TH.accent }}>{selectedIds.size} selected</span>
                {filterText && (
                  <span style={{ color: TH.textDim }}> · {filtered.length} match</span>
                )}
              </label>
              <div className="flex items-center gap-2">
                <button type="button" onClick={selectAllVisible}
                  className="px-3 py-1 rounded-full text-[10px] font-mono uppercase tracking-wider transition-all"
                  style={{ background: "transparent", color: TH.accent, border: `1px solid ${TH.accent}55` }}
                  data-testid="seed-select-all">Select all visible</button>
                <button type="button" onClick={clearSelection}
                  className="px-3 py-1 rounded-full text-[10px] font-mono uppercase tracking-wider transition-all"
                  style={{ background: "transparent", color: TH.textMute, border: `1px solid ${TH.border}` }}
                  data-testid="seed-clear-selection">Clear</button>
              </div>
            </div>

            {/* Filter box */}
            <div className="relative mb-3">
              <MagnifyingGlass size={14} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2"
                style={{ color: TH.textDim }} />
              <input type="text" placeholder="Filter by name, specialization or technique…"
                value={filterText} onChange={(e) => setFilterText(e.target.value)}
                className="w-full pl-9 pr-4 py-2.5 rounded-lg text-sm"
                style={{ background: TH.surfaceHi, border: `1px solid ${TH.border}`, color: TH.text }}
                data-testid="seed-filter" />
            </div>

            {/* List */}
            <ul className="max-h-[360px] overflow-y-auto rounded-lg space-y-1.5 p-1.5"
              style={{ background: TH.bg, border: `1px solid ${TH.border}` }}
              data-testid="seed-astrologer-list">
              {filtered.length === 0 && (
                <li className="text-center py-6 text-sm font-mono" style={{ color: TH.textDim }}>
                  No matches.
                </li>
              )}
              {filtered.map((a) => {
                const checked = selectedIds.has(a.astrologer_id);
                return (
                  <li key={a.astrologer_id}>
                    <label
                      className="flex items-center gap-3 px-3 py-2.5 rounded-lg cursor-pointer transition-all"
                      style={{
                        background: checked ? `${TH.accent}1F` : TH.surface,
                        border: `1px solid ${checked ? TH.accent : TH.border}`,
                      }}
                      data-testid={`seed-astro-item-${a.astrologer_id}`}>
                      <input type="checkbox" checked={checked}
                        onChange={() => toggle(a.astrologer_id)}
                        className="w-4 h-4 accent-yellow-400 cursor-pointer"
                        data-testid={`seed-astro-check-${a.astrologer_id}`} />
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium truncate" style={{ color: TH.text }}>
                          {a.name}
                          {a.cultural_tone === "US" && (
                            <span className="ml-1.5 inline-block px-1.5 py-0.5 rounded-full text-[9px] font-mono"
                              style={{ background: `${TH.warm}22`, color: TH.warm, border: `1px solid ${TH.warm}55` }}>
                              US
                            </span>
                          )}
                        </div>
                        <div className="text-[10px] font-mono mt-0.5 truncate" style={{ color: TH.textDim }}>
                          {a.technique || a.specialization || "—"}
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <div className="text-xs font-mono" style={{ color: TH.pop }}>
                          <Star size={10} weight="fill" className="inline mr-0.5" style={{ color: TH.accent }} />
                          {a.rating || 0}
                        </div>
                        <div className="text-[10px] font-mono" style={{ color: TH.textDim }}>
                          {a.reviews_count || 0} reviews
                        </div>
                      </div>
                    </label>
                  </li>
                );
              })}
            </ul>
          </div>

          {/* Count + span */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs uppercase tracking-wider mb-1.5 font-mono" style={{ color: TH.textMute }}>
                5-star reviews PER astrologer
              </label>
              <input type="number" min={1} max={100} value={count} onChange={(e) => setCount(e.target.value)}
                className="w-full px-4 py-3 rounded-lg text-base font-mono"
                style={{ background: TH.surfaceHi, border: `1px solid ${TH.border}`, color: TH.text }}
                data-testid="seed-input-count" />
              <div className="mt-1 text-[10px] font-mono" style={{ color: TH.textDim }}>1 – 100</div>
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wider mb-1.5 font-mono" style={{ color: TH.textMute }}>
                Spread over (days)
              </label>
              <input type="number" min={1} max={180} value={spanDays} onChange={(e) => setSpanDays(e.target.value)}
                className="w-full px-4 py-3 rounded-lg text-base font-mono"
                style={{ background: TH.surfaceHi, border: `1px solid ${TH.border}`, color: TH.text }}
                data-testid="seed-input-span" />
              <div className="mt-1 text-[10px] font-mono" style={{ color: TH.textDim }}>e.g. 28 → newest first, up to 28 days back</div>
            </div>
          </div>

          {/* Quick-pick chips */}
          <div className="flex flex-wrap gap-2">
            <span className="text-[10px] font-mono uppercase tracking-wider self-center mr-1" style={{ color: TH.textDim }}>
              Quick count:
            </span>
            {[5, 10, 20, 30, 50].map((n) => (
              <button key={n} type="button" onClick={() => setCount(n)}
                className="px-3 py-1 rounded-full text-xs font-mono transition-all"
                style={{
                  background: count === n ? TH.accent : "transparent",
                  color: count === n ? TH.bg : TH.textMute,
                  border: `1px solid ${count === n ? TH.accent : TH.border}`,
                  fontWeight: count === n ? 700 : 400,
                }}
                data-testid={`seed-quick-${n}`}>
                {n}
              </button>
            ))}
          </div>

          {/* Live total */}
          {selectedIds.size > 0 && (
            <div className="rounded-lg p-3.5 text-sm"
              style={{ background: TH.surfaceHi, border: `1px solid ${TH.border}`, color: TH.textMute }}>
              About to insert <b style={{ color: TH.accent }}>{count * selectedIds.size}</b> reviews
              ({count} × {selectedIds.size} astrologers) across the last {spanDays} days.
            </div>
          )}

          {/* Actions */}
          <div className="flex flex-wrap items-center gap-3 pt-1">
            <button type="submit" disabled={submitting || selectedIds.size === 0}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-medium text-sm transition-all disabled:opacity-50"
              style={{ background: TH.accent, color: TH.bg }}
              data-testid="seed-submit">
              <MagicWand size={14} weight="bold" />
              {submitting
                ? "Seeding…"
                : selectedIds.size > 1
                  ? `Seed ${count * selectedIds.size} reviews across ${selectedIds.size} astrologers`
                  : `Seed ${count} reviews`}
            </button>
            <button type="button" onClick={undoAll} disabled={selectedIds.size !== 1}
              title={selectedIds.size === 1 ? "Remove all admin-seeded reviews for the single selected astrologer" : "Pick exactly one astrologer to use undo"}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full font-medium text-xs transition-all disabled:opacity-30"
              style={{ background: "transparent", color: "#F87171", border: "1px solid #F87171AA" }}
              data-testid="seed-undo-all">
              <Trash size={12} weight="bold" /> Undo admin-seeded reviews (single-select)
            </button>
          </div>
        </form>

        {/* Result preview */}
        {lastResult && (
          <div className="mt-7 rounded-2xl p-6"
            style={{ background: TH.surface, border: `1px solid ${TH.green}55` }}
            data-testid="seed-result">
            <div className="flex items-center gap-2 mb-4">
              <CheckCircle size={20} weight="fill" style={{ color: TH.green }} />
              <div>
                <div className="font-medium" style={{ color: TH.green }}>
                  Added {lastResult.total_inserted} reviews across {lastResult.astrologers_seeded} astrologers
                </div>
                {lastResult.missing_astrologers?.length > 0 && (
                  <div className="text-xs font-mono mt-0.5" style={{ color: "#F87171" }}>
                    {lastResult.missing_astrologers.length} astrologer(s) skipped (not found)
                  </div>
                )}
              </div>
            </div>

            <ul className="space-y-3">
              {(lastResult.per_astrologer || []).map((row) => (
                <li key={row.astrologer_id} className="rounded-lg p-3.5"
                  style={{ background: TH.surfaceHi, border: `1px solid ${TH.border}` }}>
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div>
                      <div className="text-sm font-medium" style={{ color: TH.text }}>{row.astrologer_name}</div>
                      <div className="text-[10px] font-mono mt-0.5" style={{ color: TH.textMute }}>
                        +{row.inserted} added · new avg <b style={{ color: TH.pop }}>★ {row.new_avg_rating}</b>
                        · {row.new_reviews_count} total reviews
                      </div>
                    </div>
                  </div>
                  {(row.preview || []).length > 0 && (
                    <ul className="mt-2 space-y-1.5">
                      {row.preview.map((r) => (
                        <li key={r.review_id} className="text-xs leading-relaxed pl-3"
                          style={{ borderLeft: `2px solid ${TH.accent}66`, color: TH.textMute }}>
                          <b style={{ color: TH.text }}>{r.customer_name}</b>
                          <span style={{ color: TH.accent }}> · {"★".repeat(r.rating)}</span>
                          <span style={{ color: TH.textDim }}> · {new Date(r.created_at).toLocaleDateString("en-IN", { dateStyle: "medium" })}</span>
                          <div className="mt-0.5">{r.comment}</div>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
